import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { Pressable, ScrollView, SxProp, View, useDripsyTheme } from 'dripsy';
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { RootStackRoutes } from 'types';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { Icon } from '@/design-system/components/icons/Icon';
import { CreateCardAccountStackRoutes } from '@/features/card-account/screens/create-card-account/type';
import { Moment } from 'moment-timezone';
import { DateRangePicker } from '@/design-system/components/dateRangePicker';
import moment from 'moment-timezone';
import { useReportingTransactions } from '@/features/account-management/screens/list/hooks/useListAccounts.hook';
import { Linking } from 'react-native';
import { isEmpty } from 'lodash';
import { AlertType, UniAlert } from '@/design-system/components/alerts';

export interface DownloadAccountTransactionReportProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  walletId?: string;
}

export const DownloadAccountTransactionReportWebScreen: React.FC<DownloadAccountTransactionReportProps> = (props) => {
  const { onClose, onLayout, walletId } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { body, btnView, textMb, btn, account } = sxStyles;
  const { theme } = useDripsyTheme();

  const navigationAny = useNavigation<any>();

  const [wallet, setWallet] = useState<any>();
  const [dateRangeProps, setDateRangeProps] = useState<{ from: Moment | null; to: Moment | null }>({
    from: null,
    to: null
  });
  const handleChangeDateRange = (value: { from: Moment | null; to: Moment | null }) => {
    setDateRangeProps(value);
  };

  const [alertMessage, setAlertMessage] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { reportingAccountsTransactions } = useReportingTransactions();
  const downloadTransactionReportFunc = () => {
    setIsLoading(true);
    reportingAccountsTransactions({
      dateRangeFrom: dateRangeProps?.from ? moment(dateRangeProps?.from)?.toISOString() : undefined,
      dateRangeTo: dateRangeProps?.to ? moment(dateRangeProps?.to)?.toISOString() : undefined,
      walletId: walletId || wallet?.id
    })
      .then((response) => {
        setIsLoading(false);
        if (response.data && response.data?.url) {
          // Linking.openURL(response.data?.url);
          const newWindow = window.open(response.data?.url, '_blank');
          if (newWindow) newWindow.opener = null;
          onClose();
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setAlertMessage(error);
      });
  };

  const [viewShow, setViewShow] = useState(false);
  const onChangeView = () => {
    setViewShow(!viewShow);
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary',
        overflow: 'visible',
        borderRadius: theme.space?.$radius2 as number
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader
        sx={{
          borderTopLeftRadius: theme.space?.$radius2 as number,
          borderTopRightRadius: theme.space?.$radius2 as number,
          overflow: 'hidden'
        }}
        title={t('card-account-management::account-page.button--download-transaction-report')}
        closeModal={onClose}
      />
      <View sx={body}>
        <TextTitle sx={textMb} semiBold>
          {t('card-account-management::account-page.text--download-following-filter')}
        </TextTitle>
        <View sx={{ gap: '$5' }}>
          {!walletId ? (
            <View sx={{ gap: 4 }}>
              <TextCaption semiBold>{t('card-account-management::account-page.text--account')}</TextCaption>
              <Pressable
                onPress={() => {
                  navigationAny.navigate(RootStackRoutes.AccountManagement, {
                    screen: AccountManagementStackRoutes.DownloadTransactionReport,
                    params: {
                      screen: CreateCardAccountStackRoutes.SelectAccount,
                      onLayout,
                      onSelect: (data: any) => {
                        setWallet(data);
                      },
                      onChangeView: onChangeView,
                      borderRadiusFlag: true
                    }
                  });
                }}>
                <View sx={{ ...account, ...(wallet ? { flexDirection: 'column', py: '$2' } : {}) }}>
                  <View
                    sx={{
                      justifyContent: 'space-between',
                      display: 'flex',
                      width: '100%',
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}>
                    {wallet ? (
                      <View sx={{ gap: '$1' }}>
                        <TextBody semiBold sx={{ color: '$textPrimary' }}>
                          {wallet?.name}
                        </TextBody>
                        <TextBody sx={{ color: '$textPrimary' }}>{wallet?.accountNumber}</TextBody>
                      </View>
                    ) : (
                      <TextCaption sx={{ color: '$borderUpload' }}>
                        {t('card-account::credit-card-new-account.glossary.select-account')}
                      </TextCaption>
                    )}
                    <View sx={{ flexDirection: 'row', gap: '$1' }}>
                      <Icon name="arrowRight" />
                      {wallet ? (
                        <UniIconButton
                          icon="cross"
                          onPress={() => {
                            setWallet(undefined);
                          }}
                        />
                      ) : undefined}
                    </View>
                  </View>
                </View>
              </Pressable>
            </View>
          ) : undefined}
          <View sx={{ gap: 4 }}>
            <TextCaption semiBold>{t('earn-invest::glossary.date-range')}</TextCaption>
            <View sx={{ height: 36, width: '$80' }}>
              <DateRangePicker
                dateRangeProps={dateRangeProps}
                onValueChange={(value) => {
                  handleChangeDateRange({
                    ...dateRangeProps,
                    from: value.from ? moment(value.from) : null,
                    to: value.to ? moment(value.to) : null
                  });
                }}
              />
            </View>
          </View>
        </View>
        {viewShow && <View sx={{ height: '$1', width: '100%' }} />}
        {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
        <View sx={btnView}>
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::account-page.text--download')}
            onPress={downloadTransactionReportFunc}
            loading={isLoading}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    marginTop: 40,
    zIndex: -1
  },
  btn: {
    width: '100%',
    height: 44
  },
  account: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    py: '$5',
    px: '$4',
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderRadius: 5
  }
};
